// Configs for country study-guide landing pages (France, Germany, UK).
// One config → CountryStudyPage template renders the whole page.
//
// IMPORTANT: These pages intentionally DO NOT list specific universities,
// programs, tuition fees or intakes because those vary by profile/institution
// and change frequently. Copy uses qualified language ("depending on the
// university and program", "requirements vary…") — see business rules in
// the product brief.

import {
  Award, Globe, Users, Building2, Landmark, TrendingUp,
  Briefcase, Cpu, Wrench, Stethoscope, UtensilsCrossed, Palette, FlaskConical, Sparkles,
  Building, Home, Heart, Bus, PlaneTakeoff, FileText, Shield, GraduationCap, Coins,
  BookOpen, HelpCircle, Layers, Hammer, Scale, Plane, Wallet, Languages
} from 'lucide-react';
import { programs as ALL_PROGRAMS } from './programsData';

// Given a country slug (e.g. 'georgia'), return every program we currently offer in
// that country as a flat list of { program, country } — used by CountryStudyPage
// to render the "Programs Available in <country>" cards that link into
// /programs/<programSlug>/<countrySlug> (existing CountryUniversities page).
export const getProgramsInCountry = (countrySlug) => {
  const out = [];
  ALL_PROGRAMS.forEach((p) => {
    const country = p.countries?.find((c) => c.slug === countrySlug);
    if (country) out.push({ program: p, country });
  });
  return out;
};

// ————————————————————————————————————————————————————————————
// FRANCE
// ————————————————————————————————————————————————————————————
export const franceConfig = {
  slug: 'france',
  routePath: '/study-in-france',
  country: 'France',
  flag: '🇫🇷',
  intakeBadge: 'Admissions Open for All Intakes',
  theme: {
    primary: '#0055A4',   // French blue
    dark:    '#002654',
    accent:  '#EF4135',   // French red
    heroFrom:'#00296B',
    heroVia: '#003F88',
    heroTo:  '#00509D',
  },
  seo: {
    title: "Study in France | Universities, Courses & Admission | Patrick's Edu Care",
    description: "Explore Bachelor's and Master's study opportunities in France. Public and private institutions, wide range of programs and multiple intakes. Talk to Patrick's Edu Care for a personalised admissions plan.",
    keywords: 'Study in France, France intakes, French universities, Study abroad France, France admissions, Bachelor France, Master France',
  },
  hero: {
    headline: 'Build Your Future in France',
    subheading: "Explore Bachelor's and Master's study opportunities at leading public and private institutions in France. Find the right program based on your academic profile, career goals and budget.",
    primaryCta: 'Check Your Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Globe,     title: 'Globally recognised education',      desc: 'French qualifications are widely accepted by employers and universities worldwide.' },
    { icon: Landmark,  title: 'Public and private institutions',    desc: 'Choose from a broad landscape of public universities, grandes écoles and private higher-education institutions.' },
    { icon: Layers,    title: 'Wide range of programs',              desc: 'From business and engineering to fashion, sciences and hospitality.' },
    { icon: Users,     title: 'International student environment',   desc: 'A large and diverse international student community across major French cities.' },
    { icon: TrendingUp,title: 'European career opportunities',       desc: 'Location within the EU with access to a wide European job market — regulations and outcomes vary.' },
    { icon: Award,     title: 'Strong academic & research ecosystem',desc: 'Long-standing academic tradition with active research and industry linkages.' },
  ],
  studyAreas: [
    { icon: Briefcase,   title: 'Business & Management', desc: 'BBA, Management, Finance, Marketing, International Business, MBA and related fields.' },
    { icon: Cpu,         title: 'Computer Science & IT', desc: 'Computer Science, AI, Data Science, Cybersecurity, Software Engineering and related fields.' },
    { icon: Wrench,      title: 'Engineering',            desc: 'Mechanical, Civil, Electrical, Industrial and other engineering disciplines.' },
    { icon: Stethoscope, title: 'Healthcare',             desc: 'Healthcare, Nursing, Public Health and related programs where available.' },
    { icon: UtensilsCrossed, title: 'Hospitality & Tourism', desc: 'Hospitality Management, Tourism, Hotel Management and related fields.' },
    { icon: Palette,     title: 'Arts, Design & Fashion',  desc: 'Design, Fashion, Architecture, Creative Arts and related fields.' },
    { icon: FlaskConical,title: 'Sciences',                desc: 'Biology, Chemistry, Mathematics and related disciplines.' },
    { icon: Sparkles,    title: 'Other Programs',          desc: "Other Bachelor's and Master's programs depending on institution availability." },
  ],
  studyAreasNote: 'Program availability, tuition fees, admission criteria and intakes vary by university and program. Contact Patrick’s Edu Care for current options matching your profile.',
  publicVsPrivate: {
    public: {
      title: 'Public Institutions',
      points: [
        'Often more cost-effective',
        'Competitive admission',
        'Specific academic requirements',
        'French or English language requirements depending on program',
        'Institution-specific admission procedures',
      ],
    },
    private: {
      title: 'Private Institutions',
      points: [
        'Wide range of specialised programs',
        'Many international / English-taught options',
        'Different admission criteria',
        'Tuition varies by institution and program',
        'Potentially more flexible program choices',
      ],
    },
    footnote: 'Neither option is universally "better". The right choice depends on your profile, budget, career goals and program interest.',
  },
  eligibilityCards: [
    {
      title: "For Bachelor's Applicants",
      points: [
        'Higher Secondary / Grade 12 qualification',
        'Academic transcripts',
        'Valid passport',
        'Program-specific academic requirements',
        'Language proficiency where required',
        'Other university-specific documents',
      ],
    },
    {
      title: "For Master's Applicants",
      points: [
        "Recognised Bachelor's degree",
        'Academic transcripts',
        'Valid passport',
        'CV where required',
        'Motivation letter / SOP where required',
        'Language proficiency',
        'Program-specific requirements',
      ],
    },
  ],
  requirementsNote: 'Requirements vary by university, program and applicant profile. A profile assessment is recommended before selecting institutions.',
  language: [
    'French-taught programs may require French proficiency.',
    'English-taught programs may require proof of English proficiency (such as IELTS, TOEFL or equivalent).',
    'Specific score requirements vary by institution and program — we recommend confirming with your target universities during shortlisting.',
  ],
  cost: {
    intro: 'Costs vary significantly by city, institution and lifestyle — plan across the categories below rather than a single "total".',
    categories: [
      { icon: GraduationCap, title: 'Tuition fees' },
      { icon: Home,          title: 'Accommodation' },
      { icon: UtensilsCrossed,title: 'Food & living expenses' },
      { icon: Heart,         title: 'Health insurance' },
      { icon: Bus,           title: 'Transportation' },
      { icon: FileText,      title: 'Visa / documentation' },
      { icon: PlaneTakeoff,  title: 'Travel' },
    ],
    cta: 'Get Your Personalised Cost Estimate',
  },
  intake: {
    title: 'Intakes Throughout the Year',
    description: 'Selected universities and programs may have January / early-year intakes. Intake availability varies by institution and program.',
    secondary: 'Other intakes (such as September) may also be available depending on the institution and course.',
  },
  applicationSteps: [
    { title: 'Profile Evaluation',           desc: 'We assess your academic, budget and career profile.' },
    { title: 'Program Selection',            desc: 'Shortlist study areas that match your profile and goals.' },
    { title: 'University Shortlisting',      desc: 'Curate institutions that fit your eligibility and budget.' },
    { title: 'Document Preparation',         desc: 'Prepare transcripts, SOPs, LORs, CV and financial documents.' },
    { title: 'Application Submission',       desc: 'Submit applications to shortlisted universities.' },
    { title: 'Offer & Admission Confirmation',desc: 'Review offers and finalise your admission.' },
    { title: 'Visa Preparation',             desc: 'Prepare visa documentation and supporting evidence.' },
    { title: 'Pre-Departure & Arrival',      desc: 'Guidance on travel, accommodation and first-week logistics.' },
  ],
  visa: {
    intro: 'Guidance across the student visa journey — every application depends on individual documentation and consular decisions.',
    checklist: [
      'Document checklist',
      'Financial-document guidance',
      'Visa application preparation',
      'Appointment guidance',
      'Interview preparation where applicable',
      'Pre-departure guidance',
    ],
    disclaimer: "Visa approval is at the sole discretion of the relevant authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's Edu Care provides end-to-end guidance for students planning to study in France:",
    points: [
      'Experienced counselling',
      'Profile-based university selection',
      'Public & private university options',
      'Application support',
      'Visa guidance',
      'Pre-departure support',
      'Post-arrival assistance',
    ],
  },
  faqs: [
    { q: 'Can Indian students study in France?', a: 'Yes. France welcomes international students, including students from India, into Bachelor’s and Master’s programs. Eligibility depends on your academic profile and the institution you apply to.' },
    { q: 'Can I study in France in English?',    a: 'Yes — many private institutions and selected public universities offer English-taught programs, especially at postgraduate level. Availability varies by discipline and university.' },
    { q: 'Do I need to know French?',            a: 'It depends on the program. French-taught programs typically require proof of French proficiency; English-taught programs generally do not. Basic French is helpful for daily life.' },
    { q: "What are the Bachelor's requirements?",a: 'Generally a completed Grade 12 / Higher Secondary qualification, academic transcripts, valid passport and program-specific requirements. Language proficiency may also be needed.' },
    { q: "What are the Master's requirements?",  a: 'A recognised Bachelor’s degree, academic transcripts, a CV, motivation letter / SOP where required and language proficiency. Program-specific requirements apply.' },
    { q: 'How much does it cost?',               a: 'Costs vary significantly by institution, city and program. Public institutions are often more cost-effective; private institutions vary widely. We provide a personalised cost estimate based on your shortlist.' },
    { q: 'Can I apply to public universities?',  a: 'Yes, subject to eligibility, competitive selection and university-specific procedures. We help you assess whether public institutions fit your profile.' },
    { q: 'Can I apply to private institutions?', a: 'Yes. Private institutions often have flexible entry pathways, specialised programs and English-taught options. Admission requirements and fees vary by institution.' },
    { q: 'What documents are required?',         a: 'Passport, academic transcripts, degree certificates (if applicable), SOP, CV, language test results (where needed), financial evidence and program-specific documents. We prepare a personalised checklist.' },
    { q: 'What intake should I apply for?',      a: 'Most institutions offer September and January / early-year intakes, depending on the program. We recommend applying 4–8 months before the intended intake.' },
    { q: 'How does the student visa process work?', a: 'Once you have an admission offer, you prepare financial evidence, book a visa appointment, attend if required, and submit supporting documentation. Timelines vary by consulate.' },
    { q: 'Can I work while studying?',           a: 'International students in France are typically allowed limited part-time work under student-visa regulations. Exact permissions depend on visa type and change over time — verify current rules before you rely on them.' },
  ],
};

// ————————————————————————————————————————————————————————————
// GERMANY
// ————————————————————————————————————————————————————————————
export const germanyConfig = {
  slug: 'germany',
  routePath: '/study-in-germany',
  country: 'Germany',
  flag: '🇩🇪',
  intakeBadge: 'Admissions Open for All Intakes',
  theme: {
    primary: '#DD0000',   // German red
    dark:    '#0B0B0B',   // German black
    accent:  '#FFCC00',   // German gold
    heroFrom:'#000000',
    heroVia: '#1F1F1F',
    heroTo:  '#3D3D3D',
  },
  seo: {
    title: "Study in Germany | Universities, Courses & Admission | Patrick's Edu Care",
    description: 'Study Bachelor’s, Master’s and Ausbildung programs at German public and private institutions. Engineering, IT, business, healthcare and more — admissions open for all intakes at Patrick’s Edu Care.',
    keywords: 'Study in Germany, Germany intakes, German universities, Study abroad Germany, Bachelor Germany, Master Germany, Ausbildung Germany',
  },
  hero: {
    headline: 'Build Your Future in Germany',
    subheading: 'Explore study opportunities at German public and private universities and higher-education institutions across a wide range of disciplines.',
    primaryCta: 'Check My Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Globe,     title: 'Globally recognised education',      desc: 'German degrees are widely respected across academia and industry.' },
    { icon: Landmark,  title: 'Strong public university system',    desc: 'A well-established public HE system with strong regional depth.' },
    { icon: Building2, title: 'Public and private institutions',    desc: 'Universities, universities of applied sciences and private HE providers.' },
    { icon: Wrench,    title: 'Engineering & technology strength',  desc: 'Long-standing global reputation in engineering, manufacturing and applied sciences.' },
    { icon: Award,     title: 'Strong research environment',        desc: 'Active research institutes and industry-university linkages.' },
    { icon: BookOpen,  title: 'International study options',        desc: 'Growing catalogue of English-taught programs, particularly at Master’s level.' },
    { icon: Globe,     title: 'Central European location',          desc: 'Well-connected within the EU with a diverse student community.' },
  ],
  studyAreas: [
    { icon: Wrench,      title: 'Engineering',                 desc: 'Mechanical, Electrical, Civil, Industrial, Automotive and related disciplines.' },
    { icon: Cpu,         title: 'Computer Science & IT',       desc: 'Software Engineering, CS, IT — including AI and Data Science tracks.' },
    { icon: Sparkles,    title: 'Artificial Intelligence',     desc: 'Applied AI, Machine Learning, Robotics and adjacent fields.' },
    { icon: FlaskConical,title: 'Data Science',                desc: 'Data analytics, statistics and applied data engineering programs.' },
    { icon: Briefcase,   title: 'Business & Management',       desc: 'Management, International Business, Marketing, Finance and related programs.' },
    { icon: TrendingUp,  title: 'MBA',                          desc: 'Full-time and executive MBA programs at private and select public institutions.' },
    { icon: Coins,       title: 'Finance',                      desc: 'Finance, Banking, Financial Engineering and related programs.' },
    { icon: Stethoscope, title: 'Healthcare',                   desc: 'Public Health, Health Management and related fields — clinical routes have stricter pathways.' },
    { icon: FlaskConical,title: 'Natural Sciences',             desc: 'Physics, Chemistry, Biology, Mathematics and applied sciences.' },
    { icon: Building,    title: 'Architecture',                 desc: 'Architecture, Urban Planning and related design disciplines.' },
    { icon: Users,       title: 'Social Sciences',              desc: 'Economics, Political Science, Sociology and related programs.' },
    { icon: Sparkles,    title: 'Other Programs',               desc: "Other Bachelor's and Master's programs depending on institution availability." },
  ],
  studyAreasExtra: {
    icon: Hammer,
    title: 'Ausbildung / Vocational Education',
    desc: 'Ausbildung is a distinct, dual-education pathway with its own eligibility, German-language and application requirements. It should be assessed separately from academic Bachelor’s/Master’s routes — talk to us for a specific Ausbildung assessment.',
    href: '/ausbildung',
    linkLabel: 'Learn about Ausbildung →',
  },
  studyAreasNote: 'Program availability, tuition/semester contributions, admission criteria and intakes vary by university and program. Contact Patrick’s Edu Care for current options matching your profile.',
  publicVsPrivate: {
    public: {
      title: 'Public Universities & Applied Sciences',
      points: [
        'Generally lower tuition; semester contributions apply',
        'Competitive admission and specific academic prerequisites',
        'German or English language requirements depending on program',
        'Includes universities (research) and universities of applied sciences',
      ],
    },
    private: {
      title: 'Private Higher Education Providers',
      points: [
        'Broader range of English-taught options',
        'More flexible entry pathways in some cases',
        'Tuition varies — often higher than public institutions',
        'Specialised business, management and technology programs',
      ],
    },
    footnote: 'Note: it is a common misconception that "all German public universities are free". Tuition, semester contributions and living-cost obligations vary by state, institution and student circumstances.',
  },
  eligibilitySection: {
    title: 'Your Previous Qualification Matters',
    intro: 'German admission depends on the recognition of your previous education and the program you apply for.',
    cards: [
      {
        title: "For Bachelor's Applicants",
        points: [
          'Grade 12 / equivalent qualification, subject to recognition',
          'May require Studienkolleg (foundation) in some cases',
          'Academic transcripts and program-specific requirements',
          'Language proficiency where required',
          'Valid passport',
        ],
      },
      {
        title: "For Master's Applicants",
        points: [
          'Recognised Bachelor\'s degree in a relevant discipline',
          'Academic transcripts and CV',
          'SOP / motivation letter where required',
          'Language proficiency (German or English)',
          'Valid passport',
        ],
      },
    ],
  },
  language: [
    'German-taught programs may require proof of German proficiency (typically DSH, TestDaF or equivalent).',
    'English-taught programs may require English proficiency (IELTS, TOEFL or equivalent).',
    'Score requirements vary by institution and program — confirm during university shortlisting.',
  ],
  cost: {
    intro: 'Costs vary by state, city, institution and lifestyle. Plan across the categories below rather than a single fixed figure.',
    categories: [
      { icon: GraduationCap, title: 'Tuition / semester contributions' },
      { icon: Home,          title: 'Accommodation' },
      { icon: UtensilsCrossed,title: 'Living expenses' },
      { icon: Heart,         title: 'Health insurance' },
      { icon: FileText,      title: 'Visa requirements' },
      { icon: PlaneTakeoff,  title: 'Travel' },
      { icon: Coins,         title: 'Other expenses' },
    ],
    cta: 'Get Your Personalised Cost Estimate',
  },
  intake: {
    title: 'Admissions Open for All Intakes',
    description: 'German institutions typically offer winter and summer intakes; specific availability varies by university and program.',
    secondary: 'We recommend starting your application 6–9 months before the intended intake for a comfortable timeline.',
  },
  applicationSteps: [
    { title: 'Profile Evaluation',           desc: 'Assess your academic profile, program interest and budget.' },
    { title: 'Program Selection',            desc: 'Shortlist study areas aligned with your goals.' },
    { title: 'University Shortlisting',      desc: 'Match institutions to your eligibility and preferences.' },
    { title: 'Document Preparation',         desc: 'Transcripts, SOP/LORs, CV, language results and financial evidence.' },
    { title: 'Application Submission',       desc: 'Apply through uni-assist or direct portals as required.' },
    { title: 'Offer & Admission Confirmation',desc: 'Review admission decisions and enrol at your chosen institution.' },
    { title: 'Visa Preparation',             desc: 'Prepare visa documentation, financial evidence and blocked account where applicable.' },
    { title: 'Pre-Departure & Arrival',      desc: 'Guidance on accommodation, travel and first-week onboarding.' },
  ],
  visa: {
    intro: 'Guidance across the student-visa journey — outcomes depend on individual documentation and consular decisions.',
    checklist: [
      'Document checklist',
      'Financial-document guidance (including blocked account where required)',
      'Visa application preparation',
      'Appointment guidance',
      'Interview preparation where applicable',
      'Pre-departure guidance',
    ],
    disclaimer: "Visa approval is at the sole discretion of the relevant authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's Edu Care supports students planning academic and Ausbildung pathways to Germany:",
    points: [
      'Experienced counselling',
      'Profile-based university and program selection',
      'Public & private university options',
      'Application support (including uni-assist where applicable)',
      'Visa guidance',
      'Pre-departure support',
      'Post-arrival assistance',
    ],
  },
  faqs: [
    { q: 'Can Indian students study in Germany?',   a: 'Yes. Indian students study in Germany across Bachelor’s, Master’s and Ausbildung programs. Eligibility depends on your academic profile and the specific program.' },
    { q: 'Is Germany free for international students?', a: 'Not universally. Many public universities charge modest semester contributions rather than full tuition, and private institutions charge program-specific fees. State-specific rules also apply.' },
    { q: 'Can I study in English?',                  a: 'Yes — English-taught programs are available, especially at Master’s level. Availability at Bachelor’s level is more limited and varies by discipline.' },
    { q: 'Do I need German?',                        a: 'German is typically required for German-taught programs and for Ausbildung. English-taught programs generally do not require German for admission, but basic German is very helpful for daily life.' },
    { q: 'What is Studienkolleg?',                   a: 'Studienkolleg is a preparatory foundation course some applicants may need in order to qualify for Bachelor’s programs when their prior qualification is not directly recognised.' },
    { q: 'What is Ausbildung?',                      a: 'Ausbildung is Germany’s dual-education vocational pathway — you train in a company while studying at a vocational school. Requirements, German-language demands and application timelines are different from academic study.' },
    { q: "What are Bachelor's requirements?",        a: 'Grade 12 / equivalent qualification (subject to recognition), academic transcripts, passport, language proficiency where required and program-specific requirements. Studienkolleg may apply in some cases.' },
    { q: "What are Master's requirements?",          a: 'A recognised Bachelor’s degree in a relevant field, academic transcripts, CV, SOP/motivation letter where required, language proficiency and program-specific requirements.' },
    { q: 'How much does it cost?',                   a: 'Costs vary significantly. Public institutions often have lower tuition; private institutions have specific fees. Add accommodation, health insurance, living costs and visa/blocked-account requirements.' },
    { q: 'Can I apply to public universities?',      a: 'Yes, subject to eligibility, competitive selection and institution-specific procedures. We help assess your fit and prepare the application.' },
    { q: 'What documents are required?',             a: 'Passport, academic transcripts, degree certificates, SOP, CV, language results, financial evidence (including blocked account for visa) and program-specific documents. We build a personalised checklist.' },
    { q: 'When should I apply?',                     a: 'We recommend starting 6–9 months before your intended intake to allow time for document preparation, applications and visa processing.' },
  ],
};

// ————————————————————————————————————————————————————————————
// UK
// ————————————————————————————————————————————————————————————
export const ukConfig = {
  slug: 'uk',
  routePath: '/study-in-uk',
  country: 'the UK',
  countryShort: 'UK',
  flag: '🇬🇧',
  intakeBadge: 'Applications Open for All Intakes',
  theme: {
    primary: '#012169',   // UK navy
    dark:    '#0A1650',
    accent:  '#C8102E',   // UK red
    heroFrom:'#0A1650',
    heroVia: '#0A2A6B',
    heroTo:  '#1E3A8A',
  },
  seo: {
    title: "Study in UK | Universities, Courses & Admission | Patrick's Edu Care",
    description: "Study Bachelor's, Master's, MBA and professional programs at UK universities. Applications open for all intakes — talk to Patrick's Edu Care for eligibility, shortlisting and visa guidance.",
    keywords: 'Study in UK, UK intakes, UK universities, MBA UK, Study abroad UK, Bachelor UK, Master UK',
  },
  hero: {
    headline: 'Study in the UK',
    subheading: "Explore Bachelor's, Master's, MBA and professional study opportunities at UK universities and higher-education institutions.",
    primaryCta: 'Check Your Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Award,      title: 'Globally recognised universities', desc: 'A globally respected higher-education system.' },
    { icon: Layers,     title: 'Wide range of programs',            desc: 'Extensive catalogue across academic, professional and creative disciplines.' },
    { icon: GraduationCap, title: "Bachelor's & Master's options",  desc: 'Undergraduate, taught postgraduate and research postgraduate pathways.' },
    { icon: TrendingUp, title: 'Strong business & technology',      desc: 'World-class business schools and technology programs.' },
    { icon: Users,      title: 'International environment',         desc: 'Large and diverse international student community.' },
    { icon: Briefcase,  title: 'Industry-focused education',        desc: 'Placements, industry projects and internships in many programs.' },
    { icon: Globe,      title: 'Multiple study locations',          desc: 'Campuses across England, Scotland, Wales and Northern Ireland.' },
  ],
  studyAreas: [
    { icon: Briefcase,    title: 'Business & Management' },
    { icon: TrendingUp,   title: 'MBA' },
    { icon: Cpu,          title: 'Computer Science' },
    { icon: Sparkles,     title: 'Artificial Intelligence' },
    { icon: FlaskConical, title: 'Data Science' },
    { icon: Shield,       title: 'Cybersecurity' },
    { icon: Wrench,       title: 'Engineering' },
    { icon: Stethoscope,  title: 'Healthcare' },
    { icon: Heart,        title: 'Nursing' },
    { icon: Coins,        title: 'Finance & Accounting' },
    { icon: UtensilsCrossed, title: 'Hospitality & Tourism' },
    { icon: Scale,        title: 'Law' },
    { icon: Users,        title: 'Psychology' },
    { icon: BookOpen,     title: 'Social Sciences' },
    { icon: Sparkles,     title: 'Other Programs' },
  ],
  studyAreasCompact: true,
  studyAreasNote: 'Program availability, tuition fees, admission criteria and intakes vary by university and program. Contact Patrick’s Edu Care for current options matching your profile.',
  landscape: {
    title: 'UK Higher Education Landscape',
    intro: 'The UK higher-education system includes universities and other recognised higher-education providers. Rather than a simple public-vs-private split, institutions vary by history, specialism and awarding powers.',
    points: [
      'Universities with full degree-awarding powers',
      'Specialist institutions (business schools, art & design colleges, conservatoires)',
      'Other recognised higher-education providers',
      'Widely varying program specialisms, entry criteria and tuition',
    ],
  },
  eligibilityCards: [
    {
      title: "For Bachelor's Applicants",
      points: [
        'Grade 12 / equivalent qualification',
        'Academic transcripts',
        'English language requirement where applicable',
        'Passport',
        'Course-specific requirements',
      ],
    },
    {
      title: "For Master's Applicants",
      points: [
        "Recognised Bachelor's degree",
        'Academic transcripts',
        'English language requirement where applicable',
        'CV',
        'SOP / personal statement where required',
        'Passport',
        'Course-specific requirements',
      ],
    },
  ],
  requirementsNote: 'Requirements vary by university and course. Some universities may accept alternatives to IELTS — we confirm this per shortlisted university.',
  language: [
    'There is no single universal IELTS score for the UK. Requirements vary by university and course.',
    'Many universities accept alternatives to IELTS (such as PTE, TOEFL, university-specific tests or MOI) — availability differs by institution.',
    'We confirm accepted tests and score requirements for every shortlisted course before you apply.',
  ],
  cost: {
    intro: 'UK study costs vary by city, institution and program. Plan across the categories below rather than a single figure.',
    categories: [
      { icon: GraduationCap, title: 'Tuition' },
      { icon: Home,          title: 'Accommodation' },
      { icon: UtensilsCrossed,title: 'Living expenses' },
      { icon: FileText,      title: 'Visa' },
      { icon: Heart,         title: 'Immigration Health Surcharge (IHS) where applicable' },
      { icon: PlaneTakeoff,  title: 'Travel' },
      { icon: Coins,         title: 'Other expenses' },
    ],
    cta: 'Calculate My Study Budget',
  },
  intake: {
    title: 'Applications Open for All Intakes',
    description: 'UK universities typically offer January and September intakes; some institutions offer May/June intakes for selected programs.',
    secondary: 'Apply early — competitive courses and universities can close intakes before the deadline.',
  },
  applicationSteps: [
    { title: 'Profile Assessment',      desc: 'Academic and career profile review with your Patrick’s counsellor.' },
    { title: 'Course Selection',        desc: 'Shortlist courses aligned to your goals.' },
    { title: 'University Shortlisting', desc: 'Match universities to your eligibility, preferences and budget.' },
    { title: 'Application Preparation', desc: 'Personal statement, CV, transcripts and supporting documents.' },
    { title: 'Application Submission',  desc: 'Apply directly to universities or through UCAS where applicable.' },
    { title: 'Offer',                    desc: 'Receive conditional / unconditional offers.' },
    { title: 'Offer Acceptance',         desc: 'Accept the right offer and meet outstanding conditions.' },
    { title: 'CAS & Visa Preparation',   desc: 'Receive your CAS and prepare visa documentation & financial evidence.' },
    { title: 'Pre-Departure',            desc: 'Accommodation, travel and briefing on UK arrival essentials.' },
    { title: 'Arrival Support',          desc: 'Onboarding assistance during your first weeks in the UK.' },
  ],
  visa: {
    intro: "Patrick's provides guidance across the UK student visa journey. Outcomes depend on individual documentation and Home Office decisions.",
    checklist: [
      'Documentation',
      'Financial evidence',
      'Visa application preparation',
      'CAS-related preparation',
      'Appointment guidance',
      'Interview preparation where applicable',
      'Pre-departure guidance',
    ],
    disclaimer: "Visa approval is at the sole discretion of UK authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's Edu Care supports your UK application journey end-to-end:",
    points: [
      'Experienced counselling',
      'Profile-based university selection',
      'Application preparation and submission',
      'CAS & visa guidance',
      'Pre-departure support',
      'Post-arrival assistance',
    ],
  },
  faqs: [
    { q: 'Can Indian students study in the UK?',        a: 'Yes. Indian students routinely study at UK universities across Bachelor’s, Master’s and MBA programs. Eligibility depends on your academic profile and the specific course.' },
    { q: 'What can I study in the UK?',                 a: 'Almost any discipline — business, computer science, engineering, healthcare, law, humanities, arts, media, hospitality and more. Availability varies by university.' },
    { q: "What are Bachelor's requirements?",           a: 'Grade 12 / equivalent qualification, academic transcripts, English language proficiency where applicable, passport and course-specific requirements.' },
    { q: "What are Master's requirements?",             a: 'A recognised Bachelor’s degree, academic transcripts, English proficiency where applicable, CV, personal statement, passport and course-specific requirements.' },
    { q: 'Can I study without IELTS?',                  a: 'In some cases yes — several universities accept alternatives such as PTE, TOEFL, university-specific tests or Medium of Instruction (MOI) letters. It depends on the specific university and course.' },
    { q: 'How much does it cost?',                      a: 'It varies by university, city and course. Plan across tuition, accommodation, living expenses, visa/IHS and travel — we prepare a personalised estimate for your shortlist.' },
    { q: 'What is CAS?',                                a: 'CAS (Confirmation of Acceptance for Studies) is a unique reference issued by your UK university after you accept your offer. It is required to submit your student visa application.' },
    { q: 'What is the UK student visa process?',        a: 'After receiving your CAS, you prepare financial and supporting documents, complete the online application, provide biometrics and submit the application. Requirements may change — we confirm current process at time of application.' },
    { q: 'Can I work while studying?',                  a: 'International students in the UK are typically permitted limited part-time work under student visa conditions. Rules and hour limits can change — verify current UKVI rules before you rely on them.' },
    { q: 'What documents are required?',                a: 'Passport, academic transcripts, degree certificates (if applicable), personal statement, CV, English language results, financial evidence and course-specific documents. We prepare a personalised checklist.' },
    { q: 'What are the main intakes?',                  a: 'January and September are the main intakes. Some universities also offer May/June intakes for selected programs. Apply early for competitive courses.' },
    { q: 'How early should I apply?',                   a: 'We recommend applying 6–9 months before the intended intake so you have time for documentation, decisions and visa processing.' },
  ],
};

// ————————————————————————————————————————————————————————————
// GEORGIA
// ————————————————————————————————————————————————————————————
export const georgiaConfig = {
  slug: 'georgia',
  routePath: '/study-in-georgia',
  country: 'Georgia',
  flag: '🇬🇪',
  intakeBadge: 'February & September Intakes',
  programsFromCountry: 'georgia',
  theme: {
    primary: '#B22234',
    dark:    '#7A1620',
    accent:  '#D4AF37',
    heroFrom:'#7A1620',
    heroVia: '#8B1E2A',
    heroTo:  '#B22234',
  },
  seo: {
    title: "Study in Georgia 2026 | MBBS, Nursing & More | Patrick's Edu Care",
    description: "Study in Georgia — MBBS, BSc Nursing and more at WHO / NMC recognised universities in Tbilisi. English medium, affordable tuition, thriving Indian student community. Apply with Patrick's Edu Care.",
    keywords: 'Study in Georgia, MBBS Georgia, Nursing Georgia, Tbilisi universities, NMC approved Georgia, study abroad Georgia',
  },
  hero: {
    headline: 'Study in Georgia',
    subheading: 'MBBS, BSc Nursing and more at English-medium universities in Tbilisi. WHO & NMC-recognised medical programs, safe cities, active Indian student community.',
    primaryCta: 'Check My Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Award,      title: 'NMC & WHO recognised',       desc: 'Medical universities recognised by NMC India and WHO — FMGE / NExT eligible.' },
    { icon: Globe,      title: 'English medium',             desc: 'Programs taught in English — no local-language barrier during coursework.' },
    { icon: Wallet,     title: 'Affordable tuition',         desc: 'Significantly lower fees than Indian private colleges — cost varies by university.' },
    { icon: Shield,     title: 'Safe country',               desc: 'Low crime, welcoming culture and easy visa process for Indian students.' },
    { icon: Users,      title: 'Indian student community',   desc: 'Thousands of Indian students already studying at Georgian universities.' },
    { icon: Plane,      title: 'Direct flights',             desc: 'Easy connectivity from major Indian cities to Tbilisi.' },
  ],
  publicVsPrivate: null,
  eligibilityCards: [
    { title: "For MBBS Applicants", points: ['NEET qualified (mandatory for practising in India)', '50%+ in PCB at 12th (55% for reserved)', 'Age 17+ as on 31 Dec of admission year', 'Valid passport and academic transcripts'] },
    { title: "For Nursing / Other Bachelor's", points: ['Grade 12 / equivalent qualification', 'Program-specific subject requirements', 'Valid passport and academic transcripts', 'English language proficiency where required'] },
  ],
  requirementsNote: 'Exact requirements vary by university and program. We assess your profile and shortlist universities that match.',
  language: [
    'All partner programs are taught in English.',
    'Basic Georgian phrases are helpful for daily life but not required for admission.',
    'English proficiency requirement varies by university — we confirm during shortlisting.',
  ],
  cost: {
    intro: 'Total cost varies by university, program and lifestyle. Plan across these categories:',
    categories: [
      { icon: GraduationCap, title: 'Tuition fee' },
      { icon: Home,          title: 'Hostel / accommodation' },
      { icon: UtensilsCrossed,title: 'Food & living' },
      { icon: Heart,         title: 'Health insurance' },
      { icon: FileText,      title: 'Visa & documents' },
      { icon: PlaneTakeoff,  title: 'Travel' },
    ],
    cta: 'Get My Personalised Cost Estimate',
  },
  intake: {
    title: 'February & September Intakes',
    description: 'Most Georgian universities have February and September intakes. Some also open a mid-year intake for select programs.',
    secondary: 'We recommend applying 3–4 months before your intended intake.',
  },
  applicationSteps: [
    { title: 'Profile Assessment',   desc: 'Free 1-on-1 assessment of your academic profile and program interest.' },
    { title: 'University Shortlist', desc: 'Curated shortlist matching your budget, program and NMC recognition needs.' },
    { title: 'Document Preparation', desc: 'Passport, transcripts, NEET score, passport-size photos, financial evidence.' },
    { title: 'Application Submission', desc: 'Apply to shortlisted universities through Patrick’s.' },
    { title: 'Admission Letter',     desc: 'Receive admission / invitation letter after acceptance.' },
    { title: 'Visa Preparation',     desc: 'Prepare visa documentation and financial evidence.' },
    { title: 'Fee Payment',          desc: 'Pay tuition and other confirmation fees directly to the university.' },
    { title: 'Pre-Departure & Arrival', desc: 'Guidance on travel, hostel booking and first-week onboarding.' },
  ],
  visa: {
    intro: 'End-to-end student visa guidance for Georgia.',
    checklist: ['Document checklist', 'Financial-document guidance', 'Visa application preparation', 'Interview preparation where applicable', 'Pre-departure guidance'],
    disclaimer: "Visa approval is at the sole discretion of Georgian authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's is one of the most established consultancies for Georgia admissions:",
    points: ['Direct university partnerships', 'NEET-qualified counsellors', 'End-to-end application & visa support', 'Hostel & travel assistance', 'On-ground student support in Tbilisi', 'FMGE / NExT coaching guidance'],
  },
  faqs: [
    { q: 'Are Georgian medical universities NMC approved?', a: 'Yes — our partner medical universities in Georgia are recognised by the NMC India and WHO, making them FMGE / NExT eligible for practising in India.' },
    { q: 'Is NEET mandatory for MBBS in Georgia?',           a: 'Yes. NEET qualification is mandatory for any Indian student who plans to practise medicine in India after completing MBBS abroad.' },
    { q: 'What is the medium of instruction?',                a: 'All our partner programs in Georgia are taught in English.' },
    { q: 'How much does it cost to study in Georgia?',        a: 'Costs vary by university and program. Plan across tuition, hostel, food, insurance and travel. We share a personalised estimate for your shortlist.' },
    { q: 'What are the intakes?',                             a: 'February and September are the main intakes. Some universities also open mid-year intakes for select programs.' },
    { q: 'Is Georgia safe for Indian students?',              a: 'Yes. Georgia has low crime rates, welcoming locals and an established Indian student community.' },
    { q: 'Can I work part-time in Georgia?',                  a: 'Regulations allow limited student work under specific conditions. Rules can change — always verify current permissions at time of arrival.' },
    { q: 'What documents are required?',                      a: 'Passport, academic transcripts, NEET scorecard (for MBBS), passport-size photos and financial evidence. We prepare a personalised checklist.' },
  ],
};

// ————————————————————————————————————————————————————————————
// BULGARIA
// ————————————————————————————————————————————————————————————
export const bulgariaConfig = {
  slug: 'bulgaria',
  routePath: '/study-in-bulgaria',
  country: 'Bulgaria',
  flag: '🇧🇬',
  intakeBadge: 'September Intake',
  programsFromCountry: 'bulgaria',
  theme: {
    primary: '#00966E',
    dark:    '#00563F',
    accent:  '#D62612',
    heroFrom:'#00563F',
    heroVia: '#00755A',
    heroTo:  '#00966E',
  },
  seo: {
    title: "Study in Bulgaria 2026 | MBBS, Nursing & EU Programs | Patrick's Edu Care",
    description: 'Study in Bulgaria — MBBS, BSc Nursing and EU-recognised programs at top Bulgarian medical universities. English medium, Schengen exposure. Apply with Patrick’s Edu Care.',
    keywords: 'Study in Bulgaria, MBBS Bulgaria, Nursing Bulgaria, Bulgaria universities, EU medical degree, study abroad Bulgaria',
  },
  hero: {
    headline: 'Study in Bulgaria',
    subheading: 'EU & NMC-recognised medical programs, English-medium teaching, Schengen student experience — at established Bulgarian universities in Sofia, Plovdiv, Pleven and Varna.',
    primaryCta: 'Check My Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Award,     title: 'EU & NMC recognised',    desc: 'EU-accredited medical universities recognised by NMC India — FMGE / NExT eligible.' },
    { icon: Globe,     title: 'English medium',         desc: 'Full programs taught in English — well-established international student pathways.' },
    { icon: Landmark,  title: 'Long academic tradition',desc: 'Historic universities with decades of experience training international doctors.' },
    { icon: Plane,     title: 'Schengen exposure',       desc: 'EU location offers travel and academic exposure across Europe (subject to visa rules).' },
    { icon: Wallet,    title: 'Reasonable tuition',     desc: 'Competitive tuition for an EU-recognised degree — costs vary by university.' },
    { icon: Users,     title: 'Global student community',desc: 'Diverse international student communities at Bulgarian medical universities.' },
  ],
  publicVsPrivate: null,
  eligibilityCards: [
    { title: 'For MBBS Applicants',    points: ['NEET qualified (mandatory for practising in India)', '50%+ in PCB at 12th', 'Valid passport and academic transcripts', 'Program-specific entrance / eligibility screening'] },
    { title: "For Nursing / Other Bachelor's", points: ['Grade 12 / equivalent qualification', 'Program-specific subject requirements', 'Valid passport and academic transcripts', 'English proficiency where required'] },
  ],
  requirementsNote: 'Exact requirements vary by university and program. We assess your profile and shortlist universities that match.',
  language: [
    'Partner programs are taught in English.',
    'Some universities may require an English proficiency test — we confirm per shortlisted university.',
    'Basic Bulgarian phrases are useful for daily life but not required for admission.',
  ],
  cost: {
    intro: 'Total cost varies by university, program and lifestyle:',
    categories: [
      { icon: GraduationCap, title: 'Tuition fee' },
      { icon: Home,          title: 'Hostel / accommodation' },
      { icon: UtensilsCrossed,title: 'Food & living' },
      { icon: Heart,         title: 'Health insurance' },
      { icon: FileText,      title: 'Visa & documents' },
      { icon: PlaneTakeoff,  title: 'Travel' },
    ],
    cta: 'Get My Personalised Cost Estimate',
  },
  intake: {
    title: 'September Intake',
    description: 'Bulgarian medical universities primarily have a September intake. Some universities may open an additional intake for selected programs.',
    secondary: 'Apply 4–6 months in advance — competitive programs fill quickly.',
  },
  applicationSteps: [
    { title: 'Profile Assessment',   desc: 'Free 1-on-1 assessment of academic profile and program fit.' },
    { title: 'University Shortlist', desc: 'Curated shortlist matching your budget and preferences.' },
    { title: 'Document Preparation', desc: 'Passport, transcripts, NEET score (for MBBS), photos, financial evidence.' },
    { title: 'Entrance / Screening', desc: 'Some universities require a subject-based screening — we help prepare.' },
    { title: 'Admission Letter',     desc: 'Receive admission letter after successful selection.' },
    { title: 'Visa Preparation',     desc: 'Prepare Bulgarian student-visa documentation and evidence.' },
    { title: 'Fee Payment',          desc: 'Pay tuition and confirmation fees directly to the university.' },
    { title: 'Pre-Departure & Arrival', desc: 'Guidance on travel, hostel booking and first-week onboarding.' },
  ],
  visa: {
    intro: 'Guidance across the Bulgarian student visa journey.',
    checklist: ['Document checklist', 'Financial-document guidance', 'Visa application preparation', 'Appointment guidance', 'Interview preparation where applicable', 'Pre-departure guidance'],
    disclaimer: "Visa approval is at the sole discretion of Bulgarian authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's provides established support for Bulgaria admissions:",
    points: ['Direct university partnerships', 'Entrance-screening guidance where applicable', 'End-to-end application & visa support', 'Hostel & travel assistance', 'On-ground student support', 'FMGE / NExT coaching guidance'],
  },
  faqs: [
    { q: 'Are Bulgarian medical universities NMC approved?', a: 'Yes — our partner medical universities are recognised by NMC India and are EU-accredited, making them FMGE / NExT eligible.' },
    { q: 'Is NEET mandatory for MBBS in Bulgaria?',           a: 'Yes. NEET qualification is mandatory for Indian students who plan to practise medicine in India after MBBS abroad.' },
    { q: 'What is the medium of instruction?',                 a: 'All our partner programs in Bulgaria are taught fully in English.' },
    { q: 'How much does MBBS in Bulgaria cost?',               a: 'Costs vary significantly by university. Plan across tuition, hostel, living, insurance and travel — we share a personalised estimate.' },
    { q: 'When is the intake?',                                a: 'September is the main intake; some universities open an additional intake for selected programs.' },
    { q: 'Is there an entrance test?',                         a: 'Some Bulgarian universities require a subject-based entrance screening for MBBS. We help you prepare.' },
    { q: 'Can I work part-time in Bulgaria?',                  a: 'International students may be allowed limited part-time work under student-visa regulations. Rules vary — verify at time of arrival.' },
    { q: 'What documents are required?',                       a: 'Passport, academic transcripts, NEET scorecard (for MBBS), photos and financial evidence. We prepare a personalised checklist.' },
  ],
};

// ————————————————————————————————————————————————————————————
// MOLDOVA
// ————————————————————————————————————————————————————————————
export const moldovaConfig = {
  slug: 'moldova',
  routePath: '/study-in-moldova',
  country: 'Moldova',
  flag: '🇲🇩',
  intakeBadge: 'September Intake',
  programsFromCountry: 'moldova',
  theme: {
    primary: '#003DA5',
    dark:    '#002665',
    accent:  '#FFD200',
    heroFrom:'#002665',
    heroVia: '#003088',
    heroTo:  '#003DA5',
  },
  seo: {
    title: "Study in Moldova 2026 | MBBS at Nicolae Testemițanu | Patrick's Edu Care",
    description: 'Study MBBS in Moldova at Nicolae Testemițanu State University of Medicine & Pharmacy — WHO / NMC recognised, English medium, affordable European exposure. Apply with Patrick’s Edu Care.',
    keywords: 'Study in Moldova, MBBS Moldova, Nicolae Testemitanu, Chisinau universities, NMC approved Moldova, study abroad Moldova',
  },
  hero: {
    headline: 'Study in Moldova',
    subheading: 'WHO & NMC-recognised MBBS at Moldova’s leading state medical university — English medium, affordable, European academic exposure.',
    primaryCta: 'Check My Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Award,    title: 'NMC & WHO recognised',    desc: 'Recognised by NMC India and WHO — FMGE / NExT eligible for Indian students.' },
    { icon: Globe,    title: 'English medium',          desc: 'MBBS taught fully in English at the state medical university.' },
    { icon: Wallet,   title: 'Affordable tuition',      desc: 'Competitive tuition compared to many European destinations.' },
    { icon: Landmark, title: 'European academic style', desc: 'Structured European curriculum with hospital-based clinical training.' },
    { icon: Shield,   title: 'Safe & welcoming',        desc: 'Small, safe country with a welcoming attitude towards international students.' },
    { icon: Plane,    title: 'European gateway',        desc: 'Convenient connectivity for travel across Europe (subject to visa rules).' },
  ],
  publicVsPrivate: null,
  eligibilityCards: [
    { title: 'For MBBS Applicants', points: ['NEET qualified (mandatory for practising in India)', '50%+ in PCB at 12th', 'Valid passport and academic transcripts', 'Age 17+ as on 31 Dec of admission year'] },
  ],
  requirementsNote: 'Exact requirements vary by intake. We assess your profile and shortlist matching universities.',
  language: [
    'MBBS is taught fully in English.',
    'Basic Romanian phrases are helpful for daily life but not required for admission.',
    'English proficiency may be assessed at the university stage — we prepare you accordingly.',
  ],
  cost: {
    intro: 'Total cost varies by lifestyle and city. Plan across:',
    categories: [
      { icon: GraduationCap, title: 'Tuition fee' },
      { icon: Home,          title: 'Hostel / accommodation' },
      { icon: UtensilsCrossed,title: 'Food & living' },
      { icon: Heart,         title: 'Health insurance' },
      { icon: FileText,      title: 'Visa & documents' },
      { icon: PlaneTakeoff,  title: 'Travel' },
    ],
    cta: 'Get My Personalised Cost Estimate',
  },
  intake: {
    title: 'September Intake',
    description: 'The main MBBS intake in Moldova is in September. We recommend applying 3–4 months in advance.',
  },
  applicationSteps: [
    { title: 'Profile Assessment',      desc: 'Free 1-on-1 assessment of academic profile and MBBS fit.' },
    { title: 'University Shortlist',    desc: 'Guidance on Nicolae Testemițanu and other options as available.' },
    { title: 'Document Preparation',    desc: 'Passport, transcripts, NEET score, photos, financial evidence.' },
    { title: 'Application Submission',  desc: 'Apply to the university through Patrick’s.' },
    { title: 'Admission Letter',        desc: 'Receive admission letter after acceptance.' },
    { title: 'Visa Preparation',        desc: 'Prepare Moldovan student-visa documentation.' },
    { title: 'Fee Payment',             desc: 'Pay tuition & confirmation fees directly to the university.' },
    { title: 'Pre-Departure & Arrival', desc: 'Guidance on travel, hostel and first-week onboarding.' },
  ],
  visa: {
    intro: 'End-to-end student visa guidance for Moldova.',
    checklist: ['Document checklist', 'Financial-document guidance', 'Visa application preparation', 'Interview preparation where applicable', 'Pre-departure guidance'],
    disclaimer: "Visa approval is at the sole discretion of Moldovan authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's supports your Moldova MBBS journey:",
    points: ['University partnership guidance', 'End-to-end application & visa support', 'Document preparation help', 'Travel & hostel guidance', 'On-ground student support', 'FMGE / NExT coaching guidance'],
  },
  faqs: [
    { q: 'Is MBBS in Moldova NMC approved?',   a: 'Yes — Nicolae Testemițanu State University of Medicine & Pharmacy is recognised by NMC India and WHO, making it FMGE / NExT eligible.' },
    { q: 'Is NEET mandatory for MBBS in Moldova?', a: 'Yes. NEET qualification is mandatory for Indian students who plan to practise medicine in India after MBBS abroad.' },
    { q: 'What is the medium of instruction?', a: 'MBBS is taught fully in English.' },
    { q: 'How much does MBBS in Moldova cost?', a: 'Costs vary each year. Plan across tuition, hostel, food, insurance and travel — we share a personalised estimate.' },
    { q: 'When is the intake?', a: 'September is the main intake. Apply 3–4 months in advance.' },
    { q: 'Is Moldova safe for Indian students?', a: 'Yes. Moldova is a small, safe country with a welcoming attitude towards international students.' },
  ],
};

// ————————————————————————————————————————————————————————————
// AUSTRIA
// ————————————————————————————————————————————————————————————
export const austriaConfig = {
  slug: 'austria',
  routePath: '/study-in-austria',
  country: 'Austria',
  flag: '🇦🇹',
  intakeBadge: 'Winter & Summer Intakes',
  theme: {
    primary: '#B22234',   // Austrian red
    dark:    '#5A0F1A',
    accent:  '#F2C14E',   // warm gold
    heroFrom:'#7A1220',
    heroVia: '#5A0F1A',
    heroTo:  '#2E070E',
  },
  seo: {
    title: "Study in Austria 2026-27 | Universities, Fees & Admission | Patrick's Edu Care",
    description: "Study in Austria at world-class public universities and specialised private institutions. Bachelor's, Master's and PhD programs across engineering, business, sciences and the arts. Get personalised guidance from Patrick's Edu Care.",
    keywords: 'Study in Austria, Austria universities, Austria admissions, Study abroad Austria, Vienna universities, Austrian public universities, Bachelor Austria, Master Austria',
  },
  hero: {
    headline: 'Study in the Heart of Europe',
    subheading: 'Austria combines world-class public universities, deep research strengths and a high quality of life — with English-taught Master\'s options and one of Europe\'s most affordable tuition structures.',
    primaryCta: 'Check Your Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Landmark,  title: 'Historic public universities',       desc: 'Long-established Austrian public universities in Vienna, Graz, Innsbruck, Linz and Salzburg with globally respected qualifications.' },
    { icon: Coins,     title: 'Low public-university tuition',      desc: 'Public universities charge nominal tuition per semester for non-EU students — a fraction of UK or US fees.' },
    { icon: Cpu,       title: 'Strong technical universities',      desc: 'TU Wien and TU Graz are recognised for engineering, computer science, physics and applied sciences.' },
    { icon: Users,     title: 'International student community',    desc: 'A large share of students in Austria are international — English is widely spoken in student cities.' },
    { icon: TrendingUp,title: 'EU career opportunities',            desc: 'A Schengen location with structured post-study work options — regulations and outcomes vary by profile.' },
    { icon: Heart,     title: 'High quality of life',               desc: 'Consistently ranked among the world\'s most liveable countries, with safe cities and reliable public transport.' },
  ],
  studyAreas: [
    { icon: Cpu,           title: 'Computer Science & IT',      desc: 'Computer Science, AI, Data Science, Software Engineering, Cybersecurity and related fields.' },
    { icon: Wrench,        title: 'Engineering',                 desc: 'Mechanical, Electrical, Civil, Industrial and other engineering disciplines.' },
    { icon: Briefcase,     title: 'Business & Management',       desc: 'Business Administration, Finance, Marketing, International Business and MBA programs.' },
    { icon: FlaskConical,  title: 'Sciences & Research',         desc: 'Physics, Chemistry, Biology, Mathematics and research-oriented Master\'s / PhD tracks.' },
    { icon: Stethoscope,   title: 'Health Sciences',             desc: 'Health sciences and allied programs at selected institutions — availability varies.' },
    { icon: Palette,       title: 'Arts, Music & Design',        desc: 'Fine Arts, Music, Design and Architecture at Austria\'s renowned art universities.' },
    { icon: UtensilsCrossed,title: 'Tourism & Hospitality',     desc: 'Tourism, Hospitality, Event Management and Culinary Arts at specialised institutions.' },
    { icon: Sparkles,      title: 'Other Programs',              desc: "Other Bachelor's and Master's programs depending on institution availability." },
  ],
  studyAreasNote: 'Program availability, tuition fees and admission criteria vary by university and program. Contact Patrick\'s Edu Care for the current options that match your profile.',
  publicVsPrivate: {
    public: {
      title: 'Public Universities',
      points: [
        'Very low semester tuition for non-EU students',
        'Globally respected qualifications',
        'Competitive admission at popular programs',
        'German-taught bachelors (English at Master\'s common)',
        'Structured, academic-heavy curriculum',
      ],
    },
    private: {
      title: 'Private & Applied Sciences (FH)',
      points: [
        'Applied, industry-linked programs',
        'Many fully English-taught options',
        'Structured admissions with fixed intakes',
        'Tuition varies by institution and program',
        'Smaller class sizes',
      ],
    },
    footnote: 'Austria also has Fachhochschulen (Universities of Applied Sciences) — a strong option for career-oriented, English-taught Bachelor\'s and Master\'s programs.',
  },
  eligibilityCards: [
    {
      title: "For Bachelor's Applicants",
      points: [
        'Higher Secondary / Grade 12 qualification',
        'Academic transcripts',
        'Valid passport',
        'Program-specific academic prerequisites',
        'German or English proficiency depending on program',
        'Motivation letter for selected programs',
      ],
    },
    {
      title: "For Master's Applicants",
      points: [
        "Recognised Bachelor's degree in a relevant field",
        'Academic transcripts',
        'Valid passport',
        'CV / motivation letter / SOP',
        'English proficiency (IELTS / TOEFL) for English programs',
        'Program-specific requirements (portfolio, GMAT etc. where applicable)',
      ],
    },
  ],
  requirementsNote: 'Requirements vary by university, program and applicant profile. A profile assessment is recommended before selecting institutions.',
  language: [
    'Most Bachelor\'s programs at public universities are taught in German — B2 / C1 level proficiency is typically required.',
    'Master\'s programs and Fachhochschule programs frequently offer fully English-taught options — IELTS 6.0-6.5 or equivalent is typical.',
    'Specific score requirements vary by institution and program — confirm at shortlisting stage.',
  ],
  cost: {
    intro: 'Austria is one of Europe\'s most affordable study destinations if you choose a public university. Plan across all the categories below rather than a single "total".',
    categories: [
      { icon: GraduationCap,  title: 'Tuition fees' },
      { icon: Home,           title: 'Accommodation' },
      { icon: UtensilsCrossed,title: 'Food & living expenses' },
      { icon: Heart,          title: 'Health insurance' },
      { icon: Bus,            title: 'Transportation' },
      { icon: FileText,       title: 'Visa / documentation' },
      { icon: PlaneTakeoff,   title: 'Travel' },
    ],
    cta: 'Get Your Personalised Cost Estimate',
  },
  intake: {
    title: 'Winter Semester (October) & Summer Semester (March)',
    description: 'Most Austrian universities have two intakes — the main October (winter) intake and a smaller March (summer) intake. Application windows typically close 3-6 months before the semester begins.',
    secondary: 'Some private institutions and English-taught programs may have additional or rolling intake windows.',
  },
  applicationSteps: [
    { title: 'Profile Evaluation',             desc: 'We assess your academic background, career goals and budget.' },
    { title: 'Program Selection',              desc: 'Shortlist study areas that match your profile and eligibility.' },
    { title: 'University Shortlisting',        desc: 'Curate a mix of public and private institutions.' },
    { title: 'Document Preparation',           desc: 'Transcripts, translations, SOP, LORs, CV and financial documents.' },
    { title: 'Application Submission',         desc: 'Submit applications with all supporting documentation.' },
    { title: 'Offer & Admission Confirmation', desc: 'Review offers, pay tuition where required and accept.' },
    { title: 'Visa Preparation',               desc: 'Austrian student visa (Visa D) or residence permit preparation.' },
    { title: 'Pre-Departure & Arrival',        desc: 'Travel, accommodation, insurance and first-week guidance.' },
  ],
  visa: {
    intro: 'Guidance across the Austrian student visa (Visa D) or Residence Permit for Students journey — every application depends on individual documentation and consular decisions.',
    checklist: [
      'Document checklist',
      'Financial-proof guidance (blocked account / bank evidence)',
      'Health-insurance & accommodation guidance',
      'Visa / residence-permit application preparation',
      'Interview preparation where applicable',
      'Pre-departure guidance',
    ],
    disclaimer: "Visa approval is at the sole discretion of the relevant authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's Edu Care provides end-to-end guidance for students planning to study in Austria:",
    points: [
      'Experienced counselling',
      'Profile-based university selection',
      'Public university & FH options',
      'Application support',
      'Visa / residence-permit guidance',
      'Pre-departure support',
      'Post-arrival assistance',
    ],
  },
  faqs: [
    { q: 'Can Indian students study in Austria?', a: 'Yes. Austria welcomes international students including students from India for Bachelor\'s, Master\'s and PhD programs. Eligibility depends on your academic profile and target university.' },
    { q: 'Can I study in Austria in English?',     a: 'Yes — many Master\'s programs and Fachhochschule (FH) programs are fully English-taught. Most public-university Bachelor\'s programs are still taught in German.' },
    { q: 'Do I need to know German?',              a: 'For German-taught programs, B2 or C1 German is typically required. For English-taught programs, German is not mandatory — but basic German is very useful for daily life.' },
    { q: 'How affordable are Austrian public universities?', a: 'Public-university tuition for non-EU students is a nominal semester fee — one of the lowest in Western Europe. Living costs in Vienna and other cities remain the main expense.' },
    { q: 'What is a Fachhochschule (FH)?',         a: 'Fachhochschulen are Universities of Applied Sciences — practice-oriented institutions with structured intakes, smaller classes and strong industry linkages. Many programs are in English.' },
    { q: 'When should I start my application?',    a: 'For the October intake, start 6-9 months in advance. For the March intake, start 4-6 months in advance. Early applications give you the best shot at accommodation.' },
    { q: 'Can I work while studying?',             a: 'International students in Austria are typically allowed limited part-time work under student-visa regulations. Exact permissions depend on visa type and change over time.' },
    { q: 'Is the Austrian degree valued globally?',a: 'Yes. Austrian degrees are widely recognised in the EU and beyond, especially in engineering, sciences, business and the arts.' },
  ],
};

// ————————————————————————————————————————————————————————————
// MALTA
// ————————————————————————————————————————————————————————————
export const maltaConfig = {
  slug: 'malta',
  routePath: '/study-in-malta',
  country: 'Malta',
  flag: '🇲🇹',
  intakeBadge: 'October & February Intakes',
  theme: {
    primary: '#0057B7',   // Mediterranean blue
    dark:    '#002B5C',
    accent:  '#F1D302',   // warm sun yellow
    heroFrom:'#003C82',
    heroVia: '#00519E',
    heroTo:  '#003C82',
  },
  seo: {
    title: "Study in Malta 2026-27 | English-Medium EU Universities | Patrick's Edu Care",
    description: "Study in Malta — an English-speaking EU country with globally recognised universities, EU-accredited nursing and health programs, and an affordable Mediterranean lifestyle. Get personalised guidance from Patrick's Edu Care.",
    keywords: 'Study in Malta, Malta universities, BSc Nursing Malta, English medium EU, EU accredited Malta, Study abroad Malta, University of Malta, MCAST',
  },
  hero: {
    headline: 'Study in an English-Speaking EU Country',
    subheading: 'Malta is fully English-speaking, EU-accredited and Mediterranean. A top choice for BSc Nursing, business, IT and hospitality — with a lifestyle that rivals any European destination.',
    primaryCta: 'Check Your Eligibility',
    secondaryCta: 'Talk to a Counsellor',
  },
  why: [
    { icon: Languages, title: 'Fully English-medium',              desc: 'English is an official language of Malta and the medium of instruction across programs — no separate language exam needed for many pathways.' },
    { icon: Landmark,  title: 'EU-accredited qualifications',       desc: 'Maltese degrees are EU-recognised and travel well across Europe and internationally.' },
    { icon: Stethoscope,title:'Strong nursing & health programs',   desc: 'Malta is a well-established BSc Nursing destination — with EU-standard clinical training and pathway options across Europe.' },
    { icon: Users,     title: 'Small, safe and welcoming',          desc: 'A compact island community that\'s easy to settle into, with a large international-student population.' },
    { icon: TrendingUp,title: 'Post-study work opportunities',      desc: 'Structured post-study work options in the EU — regulations and outcomes vary by profile.' },
    { icon: Sparkles,  title: 'Mediterranean lifestyle',            desc: 'Sunshine 300+ days a year, coastal living and rich European history — a genuinely enjoyable student experience.' },
  ],
  studyAreas: [
    { icon: Stethoscope,   title: 'Nursing & Health Sciences',    desc: 'BSc Nursing, Midwifery, Public Health and allied health programs — a Patrick\'s strength in Malta.' },
    { icon: Briefcase,     title: 'Business & Management',        desc: 'Business Administration, Finance, Marketing and MBA programs.' },
    { icon: Cpu,           title: 'IT & Computing',                desc: 'Computer Science, Software Engineering, AI, Gaming and Digital Media.' },
    { icon: UtensilsCrossed,title:'Hospitality & Tourism',        desc: 'Hospitality Management, Tourism Studies and Culinary Arts at specialised institutions.' },
    { icon: Scale,         title: 'Law, Governance & Finance',    desc: 'Law, International Relations, Finance and Regulatory studies.' },
    { icon: Palette,       title: 'Arts, Media & Communication',  desc: 'Creative Arts, Media Studies, Communication and Design.' },
    { icon: FlaskConical,  title: 'Sciences',                      desc: 'Biology, Chemistry, Marine Sciences and research-oriented tracks.' },
    { icon: Sparkles,      title: 'Other Programs',                desc: "Additional Bachelor's and Master's programs depending on institution availability." },
  ],
  studyAreasNote: 'Program availability, tuition fees and admission criteria vary by institution and program. Contact Patrick\'s Edu Care for the current options that match your profile.',
  publicVsPrivate: {
    public: {
      title: 'Public University (University of Malta)',
      points: [
        'Globally recognised public university',
        'Wide range of Bachelor\'s and Master\'s programs',
        'Competitive admission at popular programs',
        'Structured academic calendar',
        'EU-standard qualifications',
      ],
    },
    private: {
      title: 'Private & Applied Institutions',
      points: [
        'Career-focused, English-taught programs',
        'Flexible intake windows',
        'Strong nursing, IT and business options',
        'Smaller class sizes with more support',
        'Tuition varies by institution and program',
      ],
    },
    footnote: 'Malta has one major public university (University of Malta) plus MCAST and a growing number of private higher-education institutions offering specialised, English-taught programs.',
  },
  eligibilityCards: [
    {
      title: "For Bachelor's Applicants (incl. BSc Nursing)",
      points: [
        'Higher Secondary / Grade 12 qualification',
        'Minimum required grades in subject-relevant streams',
        'Academic transcripts',
        'Valid passport',
        'English proficiency (IELTS / TOEFL / equivalent) where required',
        'Program-specific academic requirements',
      ],
    },
    {
      title: "For Master's Applicants",
      points: [
        "Recognised Bachelor's degree in a relevant field",
        'Academic transcripts',
        'Valid passport',
        'CV / motivation letter / SOP',
        'English proficiency where required',
        'Program-specific requirements (references, portfolio etc.)',
      ],
    },
  ],
  requirementsNote: 'Requirements vary by institution, program and applicant profile. A profile assessment is recommended before selecting universities.',
  language: [
    'All programs are taught in English — no separate language exam is required for many pathways.',
    'For selected programs, universities may still ask for IELTS 6.0-6.5, TOEFL or equivalent evidence of English proficiency.',
    'Confirm exact score requirements with your target universities during shortlisting.',
  ],
  cost: {
    intro: 'Malta is more affordable than most Western-European EU destinations. Plan across all the categories below rather than a single "total".',
    categories: [
      { icon: GraduationCap,  title: 'Tuition fees' },
      { icon: Home,           title: 'Accommodation' },
      { icon: UtensilsCrossed,title: 'Food & living expenses' },
      { icon: Heart,          title: 'Health insurance' },
      { icon: Bus,            title: 'Transportation' },
      { icon: FileText,       title: 'Visa / documentation' },
      { icon: PlaneTakeoff,   title: 'Travel' },
    ],
    cta: 'Get Your Personalised Cost Estimate',
  },
  intake: {
    title: 'October (main) & February (secondary) Intakes',
    description: 'Most universities in Malta have a main October intake and a smaller February intake. BSc Nursing typically follows the October intake with a fixed cohort size.',
    secondary: 'Some private institutions and English-taught programs offer additional rolling intake windows.',
  },
  applicationSteps: [
    { title: 'Profile Evaluation',             desc: 'We assess your academic background, career goals and program interest.' },
    { title: 'Program Selection',              desc: 'Shortlist programs that match your profile and eligibility.' },
    { title: 'University Shortlisting',        desc: 'Choose from public and private / applied institutions in Malta.' },
    { title: 'Document Preparation',           desc: 'Transcripts, English proficiency, SOP, LORs, CV and financial documents.' },
    { title: 'Application Submission',         desc: 'Submit applications with supporting documentation and application fees.' },
    { title: 'Offer & Admission Confirmation', desc: 'Review offers, accept and pay initial tuition where required.' },
    { title: 'Visa Preparation',               desc: 'Malta student visa / residence-permit preparation.' },
    { title: 'Pre-Departure & Arrival',        desc: 'Travel, accommodation, insurance and first-week guidance.' },
  ],
  visa: {
    intro: 'Guidance across the Malta student visa and residence-permit journey — every application depends on individual documentation and consular decisions.',
    checklist: [
      'Document checklist',
      'Financial-proof guidance',
      'Health-insurance & accommodation guidance',
      'Visa application preparation',
      'Interview preparation where applicable',
      'Pre-departure guidance',
    ],
    disclaimer: "Visa approval is at the sole discretion of the relevant authorities. Patrick's Edu Care does not guarantee visa outcomes.",
  },
  patricks: {
    intro: "Patrick's Edu Care provides end-to-end guidance for students planning to study in Malta:",
    points: [
      'Experienced counselling',
      'Profile-based university selection',
      'Strong BSc Nursing pathway experience',
      'Application support',
      'Visa / residence-permit guidance',
      'Pre-departure support',
      'Post-arrival assistance',
    ],
  },
  faqs: [
    { q: 'Can Indian students study in Malta?',        a: 'Yes. Malta welcomes international students, including students from India, across Bachelor\'s and Master\'s programs. Eligibility depends on your academic profile and target institution.' },
    { q: 'Is BSc Nursing in Malta a good option?',     a: 'Yes — Malta is a well-established BSc Nursing destination with EU-standard clinical training and English-medium coursework. Patrick\'s Edu Care actively supports this pathway.' },
    { q: 'Are programs in English?',                    a: 'Yes. English is an official language of Malta and the medium of instruction across programs. No separate language exam is required for many pathways.' },
    { q: 'Do I need IELTS to study in Malta?',         a: 'Some universities and programs may still require IELTS 6.0-6.5 or an equivalent English test. Others accept English-medium schooling as sufficient. Requirements vary — confirm at shortlisting stage.' },
    { q: 'Are Maltese qualifications EU-recognised?',  a: 'Yes. Malta is an EU member state and Maltese qualifications are EU-accredited, making them portable across Europe and widely recognised internationally.' },
    { q: 'When should I apply?',                        a: 'For the October intake, start 4-8 months in advance. Popular programs like BSc Nursing have fixed cohort sizes, so earlier is better.' },
    { q: 'Can I work while studying?',                  a: 'International students in Malta are typically allowed limited part-time work under student-visa regulations. Exact permissions depend on visa type and change over time.' },
    { q: 'How much does it cost to study in Malta?',   a: 'Costs vary by institution and program. Malta is generally more affordable than the UK, Ireland or most Western-European destinations. We provide a personalised cost estimate based on your shortlist.' },
  ],
};



export const studyDestinations = {
  france:   franceConfig,
  germany:  germanyConfig,
  uk:       ukConfig,
  georgia:  georgiaConfig,
  bulgaria: bulgariaConfig,
  moldova:  moldovaConfig,
  austria:  austriaConfig,
  malta:    maltaConfig,
};

// Master list shown in the Header dropdown + cross-links.
// Armenia removed (not currently active).
export const ALL_STUDY_DESTINATIONS = [
  { slug: 'georgia',  name: 'Georgia',        flag: '🇬🇪', href: '/study-in-georgia',   external: false, existing: true  },
  { slug: 'bulgaria', name: 'Bulgaria',       flag: '🇧🇬', href: '/study-in-bulgaria',  external: false, existing: true  },
  { slug: 'moldova',  name: 'Moldova',        flag: '🇲🇩', href: '/study-in-moldova',   external: false, existing: true  },
  { slug: 'malta',    name: 'Malta',          flag: '🇲🇹', href: '/study-in-malta',     external: false, existing: false },
  { slug: 'austria',  name: 'Austria',        flag: '🇦🇹', href: '/study-in-austria',   external: false, existing: false },
  { slug: 'france',   name: 'France',         flag: '🇫🇷', href: '/study-in-france',    external: false, existing: false },
  { slug: 'germany',  name: 'Germany',        flag: '🇩🇪', href: '/study-in-germany',   external: false, existing: false },
  { slug: 'uk',       name: 'United Kingdom', flag: '🇬🇧', href: '/study-in-uk',        external: false, existing: false },
];
